import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../../styles/Dashboard.css';
import Navbar from '../navbar';
import Sidebar from './../sidebar/index';
import Typography from '../../components/Typography';
import OverviewCustomCard1 from './../../components/OverviewCustomCard1';
import MilestoneList from '../../components/MilestoneList';
import TimeList from '../../components/TimeList';
import CustomRange from '../../components/CustomRange';

const Milestones = () => {
  const [user, setUser] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Format large numbers (e.g., 560000 -> $560K, 5000 -> $5K, 5833.33 -> $5.83K)
  const formatNumber = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(3)}M`;
    if (value >= 1000) {
      const thousands = value / 1000;
      return thousands % 1 === 0 ? `$${thousands}K` : `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/');
      }
    };
    fetchUser();
  }, [navigate]);

  // Calculate days remaining
  const calculateDaysRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    return diff <= 0 ? 0 : Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  // Calculate overview stats
  const calculateOverviewStats = () => {
    const investments = user?.investments?.filter(
      (inv) => inv.status === 'completed'
    ) || [];

    const totalInvested = investments.reduce((sum, inv) => sum + inv.initialInvestment, 0);
    const totalROI = investments.reduce((sum, inv) => sum + inv.totalROI, 0);
    const currentROI = investments.reduce((sum, inv) => {
      const now = new Date();
      const startDate = new Date(inv.startDate);
      const monthsElapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24 * 30));
      const currentMonthIndex = Math.min(monthsElapsed, inv.monthlyBreakdown.length - 1);
      return sum + (inv.monthlyBreakdown[currentMonthIndex]?.accumulatedReturn || 0);
    }, 0);
    const averageROI = investments.length > 0
      ? (investments.reduce((sum, inv) => sum + inv.annualROI, 0) / investments.length).toFixed(1)
      : 0;

    return { totalInvested, currentROI, totalReturn: totalROI - currentROI, averageROI };
  };

  const { totalInvested, currentROI, totalReturn, averageROI } = calculateOverviewStats();

  // Calculate time remaining
  const calculateTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  // Calculate payout progress
  const calculatePayoutProgress = (investment) => {
    if (!investment) return 0;
    const totalPaid = investment.payouts.reduce((sum, payout) => sum + payout.amount, 0);
    const totalROI = investment.totalROI;
    return totalROI > 0 ? Math.min((totalPaid / totalROI) * 100, 100) : 0;
  };

  // Get the selected active investment
  const oldestInvestment = user?.investments?.filter(
    (inv) => inv.status === 'completed'
  )[activeIndex];

  // Prepare milestones for CustomRange (exactly 6 points: 0, 5, 10, 15, 20, 25, 30)
  const milestoneInterval = oldestInvestment ? oldestInvestment.term === 12 ? 2 : Math.floor(oldestInvestment.term / 6) : 1;
  const milestones = oldestInvestment
    ? [
        { month: 0, accumulatedReturn: 0 }, // Starting point
        ...oldestInvestment.monthlyBreakdown.filter(entry => entry.month % milestoneInterval === 0),
        oldestInvestment.monthlyBreakdown[oldestInvestment.monthlyBreakdown.length - 1], // Ensure last month is included
      ].slice(0, oldestInvestment.term === 12 ? 13 : 7).map((entry) => ({
        label: entry.month === 0 ? 'Start' : `mo ${entry.month}`,
        subLabel: formatNumber(entry.accumulatedReturn),
        position: (entry.month / oldestInvestment.term) * 100,
      }))
    : [];

  const [timeRemaining, setTimeRemaining] = useState(
    oldestInvestment
      ? calculateTimeRemaining(oldestInvestment.endDate)
      : { days: 0, hours: 0, minutes: 0, seconds: 0 }
  );

  useEffect(() => {
    if (!oldestInvestment) {
      setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(oldestInvestment.endDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [oldestInvestment]);

  const progress = calculatePayoutProgress(oldestInvestment);

  const calculateCurrentAmount = (investment) => {
    if (!investment || !investment.monthlyBreakdown) return 0;
    const now = new Date();
    const startDate = new Date(investment.startDate);
    const monthsElapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24 * 30));
    const currentMonthIndex = Math.min(monthsElapsed, investment.monthlyBreakdown.length - 1);
    return investment.monthlyBreakdown[currentMonthIndex]?.accumulatedReturn || 0;
  };

  const currentAmount = oldestInvestment ? calculateCurrentAmount(oldestInvestment) : 0;

  // Handle "View" button click to navigate to investment details
  const handleViewInvestment = (investmentId) => {
    setActiveIndex(user?.investments?.filter((inv) => inv.status === 'completed').findIndex((inv) => inv._id === investmentId));
  };

  return (
    <div>
      <Navbar user={user} isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} closeSidebar={closeSidebar} />
      <div className="flex">
        <Sidebar isOpen={isSidebarOpen} closeSidebar={closeSidebar} />
        <div className="w-full h-full min-h-screen sm:p-5 bg-[#121212] border-[#353535] border-l">
          <div className="w-full sm:p-5 p-4 rounded-[4px] bg-black">
            <Typography text={'OVERVIEW'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
            <div className='mt-[25px] flex sm:gap-[10px] gap-1 sm:justify-between justify-center sm:flex-nowrap flex-wrap'>
              <OverviewCustomCard1
                icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
                  <g clipPath="url(#clip0_498_938)">
                    <path opacity="0.2" d="M30 35.625C31.9891 35.625 33.8968 34.8348 35.3033 33.4283C36.7098 32.0218 37.5 30.1141 37.5 28.125H52.5V16.875C52.5 15.8804 52.1049 14.9266 51.4016 14.2233C50.6984 13.5201 49.7446 13.125 48.75 13.125H11.25C10.2554 13.125 9.30161 13.5201 8.59835 14.2233C7.89509 14.9266 7.5 15.8804 7.5 16.875V28.125H22.5C22.5 30.1141 23.2902 32.0218 24.6967 33.4283C26.1032 34.8348 28.0109 35.625 30 35.625Z" fill="white" />
                    <path d="M48.75 13.125H11.25C9.17893 13.125 7.5 14.8039 7.5 16.875V43.125C7.5 45.1961 9.17893 46.875 11.25 46.875H48.75C50.8211 46.875 52.5 45.1961 52.5 43.125V16.875C52.5 14.8039 50.8211 13.125 48.75 13.125Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7.5 28.125H22.5C22.5 30.1141 23.2902 32.0218 24.6967 33.4283C26.1032 34.8348 28.0109 35.625 30 35.625C31.9891 35.625 33.8968 34.8348 35.3033 33.4283C36.7098 32.0218 37.5 30.1141 37.5 28.125H52.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7.5 20.625H52.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_498_938">
                      <rect width="60" height="60" fill="white" />
                    </clipPath>
                  </defs>
                </svg>}
                title={'Invested'}
                value={formatNumber(totalInvested)}
              />
              <OverviewCustomCard1
                icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
                  <g clipPath="url(#clip0_498_950)">
                    <path opacity="0.2" d="M41.2498 22.6688V29.0625C41.2498 33.2813 34.3967 36.7617 25.4365 37.3969C28.6967 38.632 32.9061 39.375 37.4998 39.375C47.8545 39.375 56.2498 35.5969 56.2498 30.9375C56.2498 26.8547 49.8092 23.4516 41.2498 22.6688Z" fill="white" />
                    <path opacity="0.2" d="M22.5 28.125C32.8553 28.125 41.25 24.3474 41.25 19.6875C41.25 15.0276 32.8553 11.25 22.5 11.25C12.1447 11.25 3.75 15.0276 3.75 19.6875C3.75 24.3474 12.1447 28.125 22.5 28.125Z" fill="white" />
                    <path d="M22.5 28.125C32.8553 28.125 41.25 24.3474 41.25 19.6875C41.25 15.0276 32.8553 11.25 22.5 11.25C12.1447 11.25 3.75 15.0276 3.75 19.6875C3.75 24.3474 12.1447 28.125 22.5 28.125Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3.75 19.6875V29.0625C3.75 33.7219 12.1453 37.5 22.5 37.5C32.8547 37.5 41.25 33.7219 41.25 29.0625V19.6875" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M15 27.4219V36.7969" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M41.2498 22.6688C49.8092 23.4516 56.2498 26.8547 56.2498 30.9375C56.2498 35.5969 47.8545 39.375 37.4998 39.375C32.9061 39.375 28.6967 38.632 25.4365 37.3969" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M18.75 37.3312V40.3125C18.75 44.9719 27.1453 48.75 37.5 48.75C47.8547 48.75 56.25 44.9719 56.25 40.3125V30.9375" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M45 38.6719V48.0469" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M30 27.4219V48.0469" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_498_950">
                      <rect width="60" height="60" fill="white" />
                    </clipPath>
                  </defs>
                </svg>}
                title={'Current'}
                value={formatNumber(currentROI)}
              />
              <OverviewCustomCard1
                icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
                  <g clipPath="url(#clip0_498_961)">
                    <path opacity="0.2" d="M7.5 11.25H48.75C49.7446 11.25 50.6984 11.6451 51.4016 12.3483C52.1049 13.0516 52.5 14.0054 52.5 15V48.75H7.5V11.25Z" fill="white" />
                    <path d="M52.5 48.75H7.5V11.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M46.875 16.875L30 33.75L22.5 26.25L7.5 41.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M46.875 26.25V16.875H37.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_498_961">
                      <rect width="60" height="60" fill="white" />
                    </clipPath>
                  </defs>
                </svg>}
                title={'Total Return'}
                value={formatNumber(totalReturn)}
              />
              <OverviewCustomCard1
                icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
                  <g clipPath="url(#clip0_498_967)">
                    <path opacity="0.2" d="M12.7641 47.2359C10.6078 45.0797 12.0375 40.5492 10.9406 37.8961C9.80156 35.1562 5.625 32.9297 5.625 30C5.625 27.0703 9.80156 24.8438 10.9406 22.1039C12.0375 19.4531 10.6078 14.9203 12.7641 12.7641C14.9203 10.6078 19.4531 12.0375 22.1039 10.9406C24.8555 9.80156 27.0703 5.625 30 5.625C32.9297 5.625 35.1562 9.80156 37.8961 10.9406C40.5492 12.0375 45.0797 10.6078 47.2359 12.7641C49.3922 14.9203 47.9625 19.4508 49.0594 22.1039C50.1984 24.8555 54.375 27.0703 54.375 30C54.375 32.9297 50.1984 35.1562 49.0594 37.8961C47.9625 40.5492 49.3922 45.0797 47.2359 47.2359C45.0797 49.3922 40.5492 47.9625 37.8961 49.0594C35.1562 50.1984 32.9297 54.375 30 54.375C27.0703 54.375 24.8438 50.1984 22.1039 49.0594C19.4531 47.9625 14.9203 49.3922 12.7641 47.2359Z" fill="white" />
                    <path d="M12.7641 47.2359C10.6078 45.0797 12.0375 40.5492 10.9406 37.8961C9.80156 35.1563 5.625 32.9297 5.625 30C5.625 27.0703 9.80156 24.8437 10.9406 22.1039C12.0375 19.4531 10.6078 14.9203 12.7641 12.7641C14.9203 10.6078 19.4531 12.0375 22.1039 10.9406C24.8555 9.80156 27.0703 5.625 30 5.625C32.9297 5.625 35.1563 9.80156 37.8961 10.9406C40.5492 12.0375 45.0797 10.6078 47.2359 12.7641C49.3922 14.9203 47.9625 19.4508 49.0594 22.1039C50.1984 24.8555 54.375 27.0703 54.375 30C54.375 32.9297 50.1984 35.1563 49.0594 37.8961C47.9625 40.5492 49.3922 45.0797 47.2359 47.2359C45.0797 49.3922 40.5492 47.9625 37.8961 49.0594C35.1563 50.1984 32.9297 54.375 30 54.375C27.0703 54.375 24.8437 50.1984 22.1039 49.0594C19.4531 47.9625 14.9203 49.3922 12.7641 47.2359Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22.5 26.25C24.5711 26.25 26.25 24.5711 26.25 22.5C26.25 20.4289 24.5711 18.75 22.5 18.75C20.4289 18.75 18.75 20.4289 18.75 22.5C18.75 24.5711 20.4289 26.25 22.5 26.25Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M37.5 41.25C39.5711 41.25 41.25 39.5711 41.25 37.5C41.25 35.4289 39.5711 33.75 37.5 33.75C35.4289 33.75 33.75 35.4289 33.75 37.5C33.75 39.5711 35.4289 41.25 37.5 41.25Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20.625 39.375L39.375 20.625" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_498_967">
                      <rect width="60" height="60" fill="white" />
                    </clipPath>
                  </defs>
                </svg>}
                title={'Average ROI'}
                value={`${averageROI}%`}
              />
            </div>
          </div>
          <div className="w-full p-5 mt-[14px] rounded-[4px] bg-black">
            <Typography text={'ACTIVE INVESTMENTS'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
            <div className="relative overflow-x-auto mt-3">
              <div className="relative h-36 scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto">
                <table className="w-full text-sm text-left rtl:text-right">
                  <thead className="text-xs text-white opacity-60">
                    <tr className="border-b border-[#151515]">
                      {['Amount invested', 'Start Date', 'Period', 'Guaranteed ROI', 'Current amount', 'Days remaining', 'Milestones'].map((heading, index) => (
                        <th key={index} scope="col" className="lg:px-6 px-1 min-w-[116px] py-3">
                          {heading && <Typography text={heading} size={'12px'} weight={400} />}
                        </th>
                      ))}
                      <th scope='col' className='lg:px-6 px-1 min-w-[116px] w-12 py-3'></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(user?.investments || []).filter(
                      (inv) => inv.status === 'completed'
                    ).map((investment, index) => {
                      const startDate = new Date(investment.startDate);
                      const endDate = new Date(investment.endDate);
                      const daysRemaining = calculateDaysRemaining(investment.endDate);
                      const currentAmount = calculateCurrentAmount(investment);
                      const periodDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
                      return (
                        <tr
                          key={index}
                          onClick={() => setActiveIndex(index)}
                          className={`border-b py-4 border-[#151515] h-[50px] text-white ${index === activeIndex ? 'bg-[#DF0F11]' : ''}`}
                        >
                          <td className="lg:px-6 px-1 min-w-[116px]">
                            <Typography className='' text={formatNumber(investment.initialInvestment)} size={'14px'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px]">
                            <Typography className='' text={startDate.toLocaleDateString()} size={'14px'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px]">
                            <Typography className='' text={`${periodDays} days`} size={'14px'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px]">
                            <Typography className='' text={`${investment.annualROI}%`} size={'14px'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px]">
                            <Typography className='' text={formatNumber(currentAmount)} size={'14px'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px]">
                            <Typography className='' text={`${daysRemaining} days`} size={'14px'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px]">
                            <Typography className='' text={investment.monthlyBreakdown?.length.toString() || '0'} size={'14px'} weight={400} />
                          </td>
                          <td className="lg:px-6 px-1 min-w-[116px] text-center">
                            <button
                              onClick={() => handleViewInvestment(investment._id)}
                              className={`flex items-center h-7 justify-center rounded-sm px-[18px] py-[10px] border-[#FFF] border-[1px] ${index === activeIndex ? 'bg-white' : 'bg-[#000000] opacity-60'}`}
                            >
                              <Typography text={"View"} color={index === activeIndex ? '#000' : '#FFF'} size={'12px'} weight={400} lineheight={'170%'} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className='w-full p-6 rounded-[4px] mt-3 bg-black'>
            <div className='grid grid-cols-11 lg:gap-4 gap-[30px]'>
              <div className="lg:col-span-7 col-span-11 block">
                <div className='flex justify-between items-center pr-10'>
                  <Typography text={'milestones'} size={'16px'} family={'Supply'} weight={400} transform={'uppercase'} color={'#FFF'} />
                  <Typography onClick={() => navigate(`/view-contract/${oldestInvestment?._id}`)} className="cursor-pointer" text={'VIEW agreement'} size={'12px'} textDecorationLine={'underline'} textDecorationStyle={'solid'} textDecorationSkipInk={'none'} textDecorationThickness={'auto'} textUnderlineOffset={'auto'} textUnderlinePosition={'from-font'} lineheight={'170%'} family={'Supply'} weight={400} transform={'uppercase'} color={'#DF0F11'} />
                </div>
                <MilestoneList title={'Track Your Investment'} className={'mt-4'} content={'Here, you can track your investment progress and recent payouts.'} />
                <CustomRange
                  milestones={milestones}
                  progress={progress}
                />
              </div>
              <div className="lg:col-span-2 md:col-span-6 col-span-11">
                <Typography lineheight={'170%'} text={'Your ROI'} size={'16px'} family={'Supply'} weight={400} color={'#FFF'} />
                <Typography className="mt-[17px]" lineheight={'150%'} text={'Track the return on your investment, along with a clear timeline showing when to expect your next payout.'} size={'14px'} weight={400} opacity={0.6} color={'#FFF'} />
                <div className="h-[1px] bg-white opacity-20 mt-[17px] w-full" />
                <div className="mt-[15px] grid grid-cols-8">
                  <div className="col-span-5">
                    <Typography lineheight={'170%'} text={'Current Investment'} size={'12px'} weight={400} color={'#DF0F11'} />
                    <Typography lineheight={'170%'} text={formatNumber(currentAmount)} size={'20px'} weight={500} family={'Supply'} color={'#FFF'} className="mt-[12px]" />
                  </div>
                  <div className="col-span-3">
                    <Typography lineheight={'170%'} text={'Target Investment'} size={'12px'} weight={400} color={'#DF0F11'} />
                    <Typography lineheight={'170%'} text={oldestInvestment ? formatNumber(oldestInvestment.totalROI) : '$0'} size={'20px'} weight={500} family={'Supply'} color={'#FFF'} className="mt-[12px]" />
                  </div>
                </div>
              </div>
              <div className="lg:col-span-2 md:col-span-5 col-span-11">
                <div className='flex items-center justify-between'>
                  <Typography lineheight={'170%'} text={'Time Remaining'} size={'16px'} family={'Supply'} weight={400} color={'#FFF'} />
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <mask id="mask0_1797_8621" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                      <rect width="14" height="14" transform="matrix(-4.37114e-08 -1 -1 4.37114e-08 14 14)" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_1797_8621)">
                      <path d="M1.36815 9.32445L2.19605 10.1523L6.9995 5.34889L11.803 10.1523L12.6309 9.32445L6.9995 3.69309L1.36815 9.32445Z" fill="white" />
                    </g>
                  </svg>
                </div>
                <div className='flex items-center justify-between sm:px-0 px-5 mt-5 gap-[18px]'>
                  <TimeList title={timeRemaining.days.toString()} content={'Days'} />
                  <TimeList title={timeRemaining.hours.toString()} content={'Hours'} />
                  <TimeList title={timeRemaining.minutes.toString()} content={'Minutes'} />
                  <TimeList title={timeRemaining.seconds.toString()} content={'Seconds'} />
                </div>
                <button onClick={() => navigate('/support')} className='w-full border-[2px] h-[28px] bg-white border-[#FFF] rounded-sm px-[10px] py-[18px] flex items-center justify-center gap-[10px]'>
                  <Typography text={'Support Chat'} size={'12px'} lineheight={'170%'} weight={400} color={'#000'} />
                </button>
              </div>
            </div>
            <div className='mt-[35px] h-[1px] opacity-10 bg-white w-full' />
            {oldestInvestment?.payouts?.map((payout, index) => (
              <React.Fragment key={index}>
                <MilestoneList
                  className={'py-[18px]'}
                  title={`Milestone ${index + 1} Completed`}
                  content={`Payout ${index + 1} — You’ve received ${formatNumber(payout.amount)} on ${payout.date ? new Date(payout.date).toLocaleDateString() : `Payout ${index + 1}`}.`}
                />
                <div className='h-[1px] opacity-10 bg-white w-full' />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Milestones;